import React from "react";
import QTextBox from "../../../../common/components/QTextBox";
import { ResponseInterface } from "../../../../interfaces/ResponseInterface";
import { config } from "../../../../config";
import { Col, Row } from "reactstrap";
import { Formik } from "formik";
import * as yup from "yup";
import QButton from "../../../../common/components/QButton";
import { useDispatch } from "react-redux";
import { setToast } from "../../../../app/rootReducer";
import SetupApi from "../../../../common/api/functions/Setup";
import { useTranslation } from "react-i18next";

interface Props {
    setOpen: (open: boolean) => void;
    setLoadingFunc: (loading: boolean) => void;
    onSuccess: () => void;
}

const BodyStripeConnectComponent: React.FC<Props> = ({ setOpen, setLoadingFunc, onSuccess }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [submitting, setSubmitting] = React.useState(false);
    const [submitError, setSubmitError] = React.useState<string | null>(null);

    const validationSchema = yup.object({
        stripe_access_token: yup
            .string()
            .required(t("stripe.accessToken"))
            .test(
                "secret-key",
                t("stripe.secretKeyRequired"),
                (value) => !value || /^sk_/i.test(value.trim())
            )
            .test(
                "not-publishable",
                t("stripe.publishableKeyRejected"),
                (value) => !value || !/^pk_/i.test(value.trim())
            ),
    });

    return (
        <Formik
            initialValues={{ stripe_access_token: "" }}
            validationSchema={validationSchema}
            onSubmit={async (values) => {
                setSubmitError(null);
                setSubmitting(true);
                try {
                    const response: ResponseInterface | any = await SetupApi.updateStripe(values);
                    const payload = response?.data;
                    if (response?.status === config.STATUS_CODE.OK && payload?.error !== true) {
                        await setOpen(false);
                        await setLoadingFunc(true);
                        onSuccess();
                        dispatch(setToast({
                            message: t("stripe.manualUpdateSuccess"),
                            type: config.TOAST_TYPE.SUCCESS,
                        }));
                        return;
                    }
                    const message = payload?.message || t("stripe.manualUpdateError");
                    setSubmitError(message);
                    dispatch(setToast({
                        message,
                        type: config.TOAST_TYPE.ERROR,
                    }));
                } catch (error: any) {
                    const message = error?.data?.message || t("stripe.manualUpdateError");
                    setSubmitError(message);
                    dispatch(setToast({
                        message,
                        type: config.TOAST_TYPE.ERROR,
                    }));
                } finally {
                    setSubmitting(false);
                }
            }}
        >
            {formik => (
                <React.Fragment>
                    <p className="text-muted mb-3">{t("stripe.manualUpdateHelp")}</p>
                    {submitError ? (
                        <div className="alert alert-danger py-2 small mb-3" role="alert">
                            {submitError}
                        </div>
                    ) : null}
                    <Row>
                        <Col>
                            <QTextBox
                                label={t("stripe.accessToken")}
                                value={formik.values.stripe_access_token}
                                onChange={(value: string) => formik.setFieldValue("stripe_access_token", value)}
                                error={formik.touched.stripe_access_token && formik.errors.stripe_access_token
                                    ? String(formik.errors.stripe_access_token)
                                    : undefined}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <div className="d-flex justify-content-end gap-2 mt-3">
                                <QButton
                                    type="default"
                                    onClick={() => setOpen(false)}
                                    content={t("common.button.cancel")}
                                />
                                <QButton
                                    type="primary"
                                    onClick={() => formik.handleSubmit()}
                                    disabled={submitting}
                                    content={submitting ? t("stripe.validatingKey") : t("common.button.submit")}
                                />
                            </div>
                        </Col>
                    </Row>
                </React.Fragment>
            )}
        </Formik>
    );
};

export default BodyStripeConnectComponent;
